import axios from 'axios';
import store from '../store';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Get the base URL for JWT refresh endpoint
const getRefreshUrl = () => {
  const baseUrl = process.env.REACT_APP_AUTH_API?.replace('/auth/api', '') || '';
  return `${baseUrl}/api/jwt/token/refresh/`;
};

// Add a request interceptor to include JWT token in all requests
axios.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state?.authReducer?.signToken?.access;

    if (token) {
      config.headers.Authorization = `JWT ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if the failed request was the refresh endpoint itself
      if (originalRequest.url?.includes('/token/refresh')) {
        // Refresh token also expired - logout
        console.log('Refresh token expired - logging out...');
        localStorage.removeItem("persist:root");
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `JWT ${token}`;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = store.getState();
      const refreshTokenValue = state?.authReducer?.signToken?.refresh;

      if (!refreshTokenValue) {
        // No refresh token available - logout
        console.log('No refresh token - logging out...');
        localStorage.removeItem("persist:root");
        window.location.href = '/';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(getRefreshUrl(), {
          refresh: refreshTokenValue
        });

        const newAccessToken = response.data.access;
        const newRefreshToken = response.data.refresh || refreshTokenValue;

        // Update the token in Redux store
        store.dispatch({
          type: 'getToken',
          payload: {
            access: newAccessToken,
            refresh: newRefreshToken
          }
        });

        // Update the authorization header
        originalRequest.headers.Authorization = `JWT ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Refresh failed - logout
        console.log('Token refresh failed - logging out...');
        localStorage.removeItem("persist:root");
        window.location.href = '/';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
